import React, { useEffect, useState } from "react";
import { Alert, Button, Drawer, Popconfirm, Spin, Table, message } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useDeleteRoleMutation, useLazyGetRoleQuery } from "../../../redux/roles/roleAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import { FormAction } from "../../../types/enums";
import { Permission, hasPermission } from "../../../types/permissions";
import type { RootState } from "../../../app/store";
import RolesCreateRoleForm from "../roles/RolesCreateRoleForm";
import type { ApiClientRoleOption } from "./apiClientRoles";
import apiClientErrorMessage from "./apiClientErrors";

type DrawerView =
  { kind: "list" } | { kind: "create" } | { kind: "edit"; role: unknown; id: string };

interface ApiClientRolesDrawerProps {
  open: boolean;
  /** The roles of the page's organization an API client may hold. */
  roles: ApiClientRoleOption[];
  rolesLoading: boolean;
  onClose: () => void;
  /** Called after a role was created, saved or deleted, so the page can re-read its list. */
  onChanged: () => void;
}

/**
 * Creates, edits and deletes the roles an API client can be given without
 * leaving the API Clients page. The create and edit forms are the Roles
 * page's own (RolesCreateRoleForm); deleting asks first. Each action needs
 * the Roles permission of its name, and LiftCloud checks it again. A role
 * that is deleted while an API client holds it clears that client's role.
 */
function ApiClientRolesDrawer({
  open,
  roles,
  rolesLoading,
  onClose,
  onChanged,
}: ApiClientRolesDrawerProps) {
  const [t] = useTranslation("global");
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const held = permissionSet ?? [];
  const canCreate = hasPermission(Permission.RolesCreate, held);
  const canUpdate = hasPermission(Permission.RolesUpdate, held);
  const canDelete = hasPermission(Permission.RolesDelete, held);

  const [view, setView] = useState<DrawerView>({ kind: "list" });
  const [error, setError] = useState("");
  const [loadingRole, setLoadingRole] = useState(false);
  const [triggerGetRole] = useLazyGetRoleQuery();
  const [deleteRole] = useDeleteRoleMutation();

  useEffect(() => {
    if (open) {
      setView({ kind: "list" });
      setError("");
    }
  }, [open]);

  const startEdit = async (id: string) => {
    setError("");
    setLoadingRole(true);
    try {
      const token = await GETJwtToken();
      const response = await triggerGetRole({ roleId: id, token }).unwrap();
      const role = response?.data ?? response;
      if (!role) throw new Error("empty");
      setView({ kind: "edit", role, id });
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.roleLoadFailed")));
    } finally {
      setLoadingRole(false);
    }
  };

  const remove = async (id: string) => {
    setError("");
    try {
      const token = await GETJwtToken();
      await deleteRole({ id, token }).unwrap();
      message.success(t("apiClients.roleDeleted"));
      onChanged();
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    }
  };

  const backToList = () => setView({ kind: "list" });

  const afterSave = () => {
    message.success(t("apiClients.roleSaved"));
    onChanged();
    backToList();
  };

  const columns = [
    { title: t("apiClients.role"), dataIndex: "label", key: "label" },
    {
      title: t("apiClients.actions"),
      key: "actions",
      render: (_: unknown, row: ApiClientRoleOption) => (
        <div className="d-flex gap-2 flex-wrap">
          {canUpdate && (
            <Button
              data-testid={`api-client-role-edit-${row.id}`}
              icon={<FontAwesomeIcon icon={faPen} className="generalStyles__mrFix" />}
              onClick={() => startEdit(row.id)}
            >
              {t("apiClients.editRole")}
            </Button>
          )}
          {canDelete && (
            <Popconfirm
              title={t("apiClients.deleteRoleConfirm", { name: row.label })}
              okText={t("apiClients.delete")}
              cancelText={t("apiClients.cancel")}
              okButtonProps={
                {
                  danger: true,
                  "data-testid": `api-client-role-delete-confirm-${row.id}`,
                } as Record<string, unknown>
              }
              onConfirm={() => remove(row.id)}
            >
              <Button
                danger
                data-testid={`api-client-role-delete-${row.id}`}
                icon={<FontAwesomeIcon icon={faTrash} className="generalStyles__mrFix" />}
              >
                {t("apiClients.delete")}
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  let body: React.ReactNode;
  if (view.kind === "create") {
    body = (
      <RolesCreateRoleForm
        mode={FormAction.Create}
        onCancel={backToList}
        onSaveSuccess={afterSave}
      />
    );
  } else if (view.kind === "edit") {
    body = (
      <RolesCreateRoleForm
        key={view.id}
        mode={FormAction.Edit}
        roleId={view.id}
        initialRole={view.role}
        onCancel={backToList}
        onSaveSuccess={afterSave}
      />
    );
  } else {
    body = (
      <div data-testid="api-client-roles-list">
        {error && (
          <Alert
            type="error"
            showIcon
            closable
            message={error}
            onClose={() => setError("")}
            className="mb-3"
            data-testid="api-client-roles-error"
          />
        )}
        {canCreate && (
          <Button
            type="primary"
            className="mb-3"
            data-testid="api-client-role-create"
            icon={<FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />}
            onClick={() => setView({ kind: "create" })}
          >
            {t("apiClients.createRole")}
          </Button>
        )}
        <Spin spinning={loadingRole}>
          <Table
            rowKey="id"
            loading={rolesLoading}
            dataSource={roles}
            columns={columns}
            pagination={false}
            locale={{ emptyText: t("apiClients.noRoles") }}
          />
        </Spin>
      </div>
    );
  }

  return (
    <Drawer
      open={open}
      title={t("apiClients.manageRolesTitle")}
      width={view.kind === "list" ? 520 : 960}
      onClose={onClose}
      destroyOnClose
    >
      <div data-testid="api-client-roles-drawer">{body}</div>
    </Drawer>
  );
}

export default ApiClientRolesDrawer;
